(function () {
  if (window.IcedropDiscountState) return;

  const couponStorageKey = 'icedrop-main-product-coupon';
  const percentageStorageKey = 'icedrop-discount-percentages';
  const suppressedCouponStorageKey = 'icedrop-main-product-coupon-suppressed';
  const defaultKnownPercentages = {
    aktion: 10
  };
  const excludedIceKeywords = ['deckel', 'ersatz', 'strohhalm', 'dichtung', 'reinigungstabletten', 'bubble clean', 'zubehör', 'zubehoer', 'bundle', 'set'];
  const eligibleIceKeywords = ['thermo cup', 'thermocup', 'trinkflasche', 'thermo tasse', 'thermotasse', 'thermo shaker', 'thermoshaker', 'protein shaker', 'cup', 'bottle', 'shaker', 'tasse'];
  const icedropCouponTiers = { oneToTwo: 20, threeToFour: 25, fivePlus: 30 };
  let cartStateCache = null;
  let cartStateCacheAt = 0;
  let cartStatePromise = null;
  let cartStatePromiseAt = 0;

  const isPricePreviewEnabled = () => window.IcedropDiscountConfig?.pricePreviewsEnabled !== false;

  const normalizeCode = (value) => String(value || '').trim().toLowerCase();

  const decodeDiscountValue = (value) => {
    try {
      return decodeURIComponent(String(value || '').replace(/\+/g, ' ')).trim();
    } catch (error) {
      return String(value || '').trim();
    }
  };

  const readCookie = (name) => {
    const cookieName = String(name || '').trim();
    if (!cookieName || !document.cookie) return '';

    return document.cookie
      .split(';')
      .map((cookie) => cookie.trim())
      .find((cookie) => cookie.startsWith(`${cookieName}=`))
      ?.slice(cookieName.length + 1) || '';
  };

  const readShopifyDiscountCode = () => {
    const pathMatch = window.location.pathname.match(/^\/discount\/([^/?#]+)/i);
    if (pathMatch?.[1]) return decodeDiscountValue(pathMatch[1]);

    const searchParams = new URLSearchParams(window.location.search);
    const queryCode = searchParams.get('discount')
      || searchParams.get('discount_code')
      || searchParams.get('discountCode');
    if (queryCode) return decodeDiscountValue(queryCode);

    const cookieCode = readCookie('discount_code')
      || readCookie('discountCode')
      || readCookie('_shopify_discount')
      || readCookie('discount');

    if (!cookieCode) return '';

    return decodeDiscountValue(cookieCode.split(',')[0]);
  };

  const parsePercentage = (value) => {
    const rawValue = typeof value === 'object' && value !== null
      ? (value.percentage || value.value || value.amount || '')
      : value;
    const normalized = String(rawValue || '').replace('%', '').replace(',', '.').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const readJsonStorage = (key, fallback = null) => {
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch (error) {
      return fallback;
    }
  };

  const writeJsonStorage = (key, value) => {
    try {
      window.sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
    }
  };

  const seedStoredCouponFromShopifyDiscount = () => {
    const code = readShopifyDiscountCode();
    if (!code) return readJsonStorage(couponStorageKey, null);

    const suppressedCoupon = readJsonStorage(suppressedCouponStorageKey, null);
    if (
      normalizeCode(suppressedCoupon?.code) === normalizeCode(code)
      && Date.now() - Number(suppressedCoupon?.updatedAt || 0) < 60 * 60 * 1000
    ) {
      return readJsonStorage(couponStorageKey, null);
    }

    const currentCoupon = readJsonStorage(couponStorageKey, null);
    if (normalizeCode(currentCoupon?.code) === normalizeCode(code) && currentCoupon?.status) {
      return currentCoupon;
    }

    const nextCoupon = {
      code,
      status: 'pending',
      source: 'shopify-discount-link',
      updatedAt: Date.now()
    };

    writeJsonStorage(couponStorageKey, nextCoupon);
    return nextCoupon;
  };

  const readStoredCoupon = () => seedStoredCouponFromShopifyDiscount();

  const clearStoredCoupon = () => {
    try {
      const currentCoupon = readJsonStorage(couponStorageKey, null);
      const shopifyDiscountCode = readShopifyDiscountCode();
      const codeToSuppress = currentCoupon?.code || shopifyDiscountCode;
      if (codeToSuppress) {
        writeJsonStorage(suppressedCouponStorageKey, {
          code: codeToSuppress,
          updatedAt: Date.now()
        });
      }
      window.sessionStorage.removeItem(couponStorageKey);
    } catch (error) {
    }
  };

  const setCartState = (cartState) => {
    if (!cartState || typeof cartState !== 'object') return cartState;
    cartStateCache = cartState;
    cartStateCacheAt = Date.now();
    return cartState;
  };

  const getCartState = async (options = {}) => {
    // fresh: always a new request (never an in-flight one that may predate a cart change the caller just made);
    // later forced calls within 400 ms join it.
    const fresh = Boolean(options.fresh);
    const force = fresh || Boolean(options.force);
    const maxAge = Number.isFinite(Number(options.maxAge)) ? Number(options.maxAge) : 1200;
    const now = Date.now();

    if (!force && cartStateCache && now - cartStateCacheAt <= maxAge) {
      return cartStateCache;
    }

    // Reuse an in-flight request even for forced refreshes when it started
    // moments ago; several modules request the cart at page load and each
    // extra /cart.js round trip only delays the first interaction on mobile.
    if (!fresh && cartStatePromise && (!force || now - cartStatePromiseAt <= 400)) {
      return cartStatePromise;
    }

    const cartUrl = window.theme?.routes?.cart_url;
    if (!cartUrl) return null;

    // Only the latest request writes the cache; an older answer still resolves for its own callers.
    const request = fetch(`${cartUrl}.js`, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error('Cart state fetch failed');
        return response.json();
      })
      .then((cartState) => (cartStatePromise === request ? setCartState(cartState) : cartState))
      .finally(() => {
        if (cartStatePromise === request) cartStatePromise = null;
      });
    cartStatePromiseAt = now;
    cartStatePromise = request;

    return request;
  };

  const readKnownPercentages = () => ({
    ...defaultKnownPercentages,
    ...(readJsonStorage(percentageStorageKey, {}) || {})
  });

  const rememberDiscountPercentage = (code, percentage) => {
    const normalizedCode = normalizeCode(code);
    const parsedPercentage = parsePercentage(percentage);
    if (!normalizedCode || parsedPercentage <= 0) return;
    writeJsonStorage(percentageStorageKey, {
      ...readKnownPercentages(),
      [normalizedCode]: parsedPercentage
    });
  };

  const getKnownDiscountPercentage = (code) => {
    const normalizedCode = normalizeCode(code);
    if (!normalizedCode) return 0;
    return parsePercentage(readKnownPercentages()[normalizedCode]);
  };

  const getApplicableDiscountCodes = (cartState) => {
    if (!Array.isArray(cartState?.discount_codes)) return [];
    return cartState.discount_codes
      .filter((discount) => discount?.applicable)
      .map((discount) => String(discount.code || '').trim())
      .filter(Boolean);
  };

  const getLineLevelDiscountAllocations = (item) => {
    if (Array.isArray(item?.line_level_discount_allocations)) return item.line_level_discount_allocations;
    if (Array.isArray(item?.discounts)) return item.discounts;
    return [];
  };

  const getAllocationCode = (allocation) => String(allocation?.discount_application?.title || allocation?.title || allocation?.code || '').trim();

  const getAllocationPercentage = (allocation) => {
    const discountApplication = allocation?.discount_application || allocation || {};
    return parsePercentage(
      discountApplication?.value?.percentage
        || discountApplication?.value
        || discountApplication?.percentage
        || ''
    );
  };

  const getItemHandle = (item) => {
    const explicitHandle = String(item?.handle || item?.product_handle || '').trim().toLowerCase();
    if (explicitHandle) return explicitHandle;
    const match = String(item?.url || '').match(/\/products\/([^/?#]+)/i);
    return match?.[1] ? match[1].toLowerCase() : '';
  };

  const getItemTitle = (item) => String(item?.product_title || item?.title || '').trim().toLowerCase();

  const isIceEligibleHandleTitle = (handle, title) => {
    const normalizedHandle = String(handle || '').trim().toLowerCase();
    const normalizedTitle = String(title || '').trim().toLowerCase();
    if (!normalizedHandle && !normalizedTitle) return false;
    if (excludedIceKeywords.some((keyword) => normalizedHandle.includes(keyword) || normalizedTitle.includes(keyword))) return false;
    return eligibleIceKeywords.some((keyword) => normalizedHandle.includes(keyword) || normalizedTitle.includes(keyword));
  };

  const isIceEligibleItem = (item) => isIceEligibleHandleTitle(getItemHandle(item), getItemTitle(item));

  const isIceEligibleCard = (card) => {
    const handle = String(card?.getAttribute?.('data-product-handle') || '').trim().toLowerCase();
    const title = String(card?.getAttribute?.('data-product-title') || '').trim().toLowerCase();
    return isIceEligibleHandleTitle(handle, title);
  };

  const getIceTierPercentage = (eligibleQuantity) => {
    if (eligibleQuantity >= 5) return icedropCouponTiers.fivePlus;
    if (eligibleQuantity >= 3) return icedropCouponTiers.threeToFour;
    if (eligibleQuantity >= 1) return icedropCouponTiers.oneToTwo;
    return 0;
  };

  const getCartPercentageDiscounts = (cartState, options = {}) => {
    if (!isPricePreviewEnabled()) return [];

    const excludeCodes = new Set((options.excludeCodes || ['ice']).map(normalizeCode));
    const applicableCodes = getApplicableDiscountCodes(cartState).map(normalizeCode);
    const applications = Array.isArray(cartState?.cart_level_discount_applications) ? cartState.cart_level_discount_applications : [];
    const discountsByCode = new Map();
    const knownPercentages = readKnownPercentages();

    applications.forEach((application) => {
      if (application?.type !== 'discount_code' || application?.value_type !== 'percentage') return;
      const code = String(application.title || '').trim();
      const normalizedCode = normalizeCode(code);
      if (!normalizedCode || excludeCodes.has(normalizedCode)) return;
      if (applicableCodes.length && !applicableCodes.includes(normalizedCode)) return;
      const percentage = parsePercentage(application.value);
      if (percentage <= 0) return;
      rememberDiscountPercentage(normalizedCode, percentage);
      discountsByCode.set(normalizedCode, { code, percentage });
    });

    (Array.isArray(cartState?.items) ? cartState.items : []).forEach((item) => {
      getLineLevelDiscountAllocations(item).forEach((allocation) => {
        const discountApplication = allocation?.discount_application || allocation || {};
        if (discountApplication?.type && discountApplication.type !== 'discount_code') return;
        if (discountApplication?.value_type && discountApplication.value_type !== 'percentage') return;

        const code = getAllocationCode(allocation);
        const normalizedCode = normalizeCode(code);
        if (!normalizedCode || excludeCodes.has(normalizedCode)) return;
        if (applicableCodes.length && !applicableCodes.includes(normalizedCode)) return;

        const percentage = getAllocationPercentage(allocation);
        if (percentage <= 0 || discountsByCode.has(normalizedCode)) return;

        rememberDiscountPercentage(normalizedCode, percentage);
        discountsByCode.set(normalizedCode, { code, percentage });
      });
    });

    applicableCodes.forEach((normalizedCode) => {
      if (!normalizedCode || excludeCodes.has(normalizedCode) || discountsByCode.has(normalizedCode)) return;
      const percentage = parsePercentage(knownPercentages[normalizedCode]);
      if (percentage > 0) {
        discountsByCode.set(normalizedCode, { code: normalizedCode, percentage });
      }
    });

    const storedCoupon = options.storedCoupon === undefined ? readStoredCoupon() : options.storedCoupon;
    const storedCode = normalizeCode(storedCoupon?.code);
    if (storedCode && !excludeCodes.has(storedCode) && !discountsByCode.has(storedCode)) {
      const percentage = parsePercentage(knownPercentages[storedCode]);
      if (percentage > 0 && (storedCoupon?.status === 'pending' || storedCoupon?.status === 'applied')) {
        discountsByCode.set(storedCode, { code: storedCoupon.code, percentage });
      }
    }

    return Array.from(discountsByCode.values());
  };

  const getIceProductDiscountPercentage = (cartState, options = {}) => {
    if (!isPricePreviewEnabled()) return 0;

    const storedCoupon = options.storedCoupon === undefined ? readStoredCoupon() : options.storedCoupon;
    const applicableCodes = getApplicableDiscountCodes(cartState).map(normalizeCode);
    const hasIceApplied = applicableCodes.includes('ice');

    if (!hasIceApplied) {
      if (normalizeCode(storedCoupon?.code) === 'ice' && (storedCoupon?.status === 'pending' || storedCoupon?.status === 'applied')) {
        return getIceTierPercentage(1);
      }
      return 0;
    }

    const matchedPercentages = (Array.isArray(cartState?.items) ? cartState.items : []).flatMap((item) => {
      return getLineLevelDiscountAllocations(item)
        .filter((allocation) => normalizeCode(getAllocationCode(allocation)) === 'ice')
        .map((allocation) => getAllocationPercentage(allocation))
        .filter((value) => value > 0);
    });

    if (matchedPercentages.length > 0) {
      return Math.max(...matchedPercentages);
    }

    const eligibleQuantity = (Array.isArray(cartState?.items) ? cartState.items : []).reduce((sum, item) => {
      return sum + (isIceEligibleItem(item) ? Number(item.quantity) || 0 : 0);
    }, 0);

    return getIceTierPercentage(eligibleQuantity || 1);
  };

  const getCombinedMultiplier = (discounts) => {
    const seenCodes = new Set();
    return (Array.isArray(discounts) ? discounts : []).reduce((multiplier, discount) => {
      const normalizedCode = normalizeCode(discount?.code);
      if (!normalizedCode || seenCodes.has(normalizedCode)) return multiplier;
      seenCodes.add(normalizedCode);
      const percentage = parsePercentage(discount?.percentage);
      if (percentage <= 0) return multiplier;
      return multiplier * ((100 - percentage) / 100);
    }, 1);
  };

  const getState = (cartState, options = {}) => {
    const storedCoupon = options.storedCoupon === undefined ? readStoredCoupon() : options.storedCoupon;
    if (!isPricePreviewEnabled()) {
      return {
        applicableCodes: getApplicableDiscountCodes(cartState),
        orderPercentageDiscounts: [],
        orderMultiplier: 1,
        iceProductPercentage: 0,
        storedCoupon
      };
    }

    const orderPercentageDiscounts = getCartPercentageDiscounts(cartState, options);
    const iceProductPercentage = getIceProductDiscountPercentage(cartState, { storedCoupon });

    return {
      applicableCodes: getApplicableDiscountCodes(cartState),
      orderPercentageDiscounts,
      orderMultiplier: getCombinedMultiplier(orderPercentageDiscounts),
      iceProductPercentage,
      storedCoupon
    };
  };

  window.IcedropDiscountState = {
    storageKey: couponStorageKey,
    normalizeCode,
    parsePercentage,
    isPricePreviewEnabled,
    readShopifyDiscountCode,
    readStoredCoupon,
    clearStoredCoupon,
    rememberDiscountPercentage,
    getCartState,
    setCartState,
    getKnownDiscountPercentage,
    getApplicableDiscountCodes,
    getLineLevelDiscountAllocations,
    getAllocationCode,
    getAllocationPercentage,
    getIceTierPercentage,
    isIceEligibleHandleTitle,
    isIceEligibleItem,
    isIceEligibleCard,
    getCartPercentageDiscounts,
    getIceProductDiscountPercentage,
    getCombinedMultiplier,
    getState
  };
})();
